import {useCallback, useContext, useMemo} from 'react'

import {
  TableContext,
  TableStateSelectedRowIdsContext,
  type TableStateSelectedRowIdsContextValue,
} from '../../components/react_table/table-provider'
import type {MemexItemModel} from '../../models/memex-item-model'
import {rowGetCanSelect} from './row-api'
import type {Row, Table} from './types'

/**
 * Returns a function to deselect all rows in the table.
 *
 * This provides compatibility with both v7 and v8 of React Table
 */
export function useTableDeselectAllRows(): () => void {
  return useDeselectAllRowsV8()
}

// Temporary private hook for Table v8 usage
function useDeselectAllRowsV8(): () => void {
  const setRowSelection = useSetRowSelection()
  const selectedRowIds = useTableSelectedRowIds()
  const noSelection = Object.keys(selectedRowIds).filter(Boolean).length === 0
  return useCallback(() => {
    if (noSelection) return
    setRowSelection([])
  }, [noSelection, setRowSelection])
}

/**
 * Returns a function to select all rows in the table.
 *
 * This provides compatibility with both v7 and v8 of React Table
 */
export function useTableSelectAllRows(): () => void {
  const table = useContext(TableContext)
  if (!table) {
    throw new Error('Must use `useTableSelectAllRows` in child of `<TableProvider>')
  }
  return useSelectAllRowsV8(table)
}

// Temporary private hook for Table v8 usage
function useSelectAllRowsV8(table: Table<MemexItemModel>): () => void {
  const setRowSelection = useSetRowSelection()
  const flatRows = table.getRowModel().flatRows
  const selectedRowIds = useMemo(() => flatRows.map(row => row.id), [flatRows])
  return useCallback(() => {
    setRowSelection(selectedRowIds)
  }, [selectedRowIds, setRowSelection])
}

/**
 * Set the selected rows for the table, replacing any existing selection.
 * @param selectedRowIds - Array of row IDs to select.
 *
 * To deselect all rows, pass an empty array.
 */
export function useSetRowSelection(): (selectedRowIds: Array<Row<MemexItemModel>['id']>) => void {
  const table = useContext(TableContext)
  if (!table) {
    throw new Error('Must use `useSetRowSelection` in child of `<TableProvider>')
  }
  const {setRowSelection} = table
  const {rowsById} = table.getRowModel()
  return useCallback(
    (selectedRowIds: Array<Row<MemexItemModel>['id']>) => {
      const newSelection = selectedRowIds
        // Ensure only selectable rows are included
        .filter(id => rowsById[id] && rowGetCanSelect(rowsById[id]))
        .reduce((acc, id) => ({...acc, [id]: true}), {} as Record<string, boolean>)
      setRowSelection(newSelection)
    },
    [rowsById, setRowSelection],
  )
}

/**
 * Get the table's current selected row IDs.
 */
export function useTableSelectedRowIds(): TableStateSelectedRowIdsContextValue {
  const selectedRowIds = useContext(TableStateSelectedRowIdsContext)
  if (!selectedRowIds) {
    throw new Error('Must use `useTableSelectedRowIds` in child of `<TableProvider>')
  }
  return selectedRowIds
}

/**
 * Returns the currently selected flat rows for the table.
 *
 * This is an alternative to table.getSelectedRowModel().flatRows
 * which is sometimes returning stale data. It may be possible to use
 * table.getSelectedRowModel().flatRows once the v8 migration is complete.
 */
export function useTableSelectedFlatRows(): Array<Row<MemexItemModel>> {
  const selectedRowIds = useContext(TableStateSelectedRowIdsContext)
  const table = useContext(TableContext)
  if (!selectedRowIds || !table) {
    throw new Error('Must use `useTableSelectedFlatRows` in child of `<TableProvider>')
  }
  const {flatRows} = table.getRowModel()
  const selectedFlatRows = useMemo(() => {
    return flatRows.filter(row => selectedRowIds[row.id])
  }, [flatRows, selectedRowIds])
  return selectedFlatRows
}
